import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { connect, ConnectedProps } from "react-redux";
import { RootState, store } from "../../../store";
import DiscoverCard from "@/components/Discover/DiscoverCard";
import { Filter, Modal, Skeleton, Tooltip } from "../../../components";
import Pagination from "@/components/Table/Pagination";
import FilterRail from "@/components/MediaList/FilterRail";
import {
  AppliedFilterChips,
  FilterSheet,
} from "@/components/MediaList/FilterSheet";
import {
  getDiscoverListMedia,
  getDiscoverWiredListMedia,
  getListMedia,
} from "@/store/media/media.action";
import { updateModal } from "@/store/app/app.slice";
import { setMediaDetails } from "@/store/media/media.slice";
import GeneralPlayerWrapper from "@/components/Player/GeneralPlayerWrapper";
import { updatePreviousFilters } from "@/store/media/media.action";
import { getUserSettings, getListUser } from "@/store/user/user.action";
import { getListAgencies } from "@/store/constants/constants.action";
import { setMediaList } from "@/store/media/media.slice";
import { NoContentFound } from "@/utils/ui/NoContent";
import { Scissors, Download, Layers2 } from "lucide-react";
import { MediaModel } from "@/database";
import {
  DISCOVER_NAV_TABS,
  DISCOVER_TAB_MAPPING,
} from "@/utils/constants/uiConstants";
import { useSearchParams } from "react-router-dom";
import { useDiscoverFilters } from "@/components/Discover/hooks/useDiscoverFilters";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import { getAllowedTeamIds } from "@/utils/mediautils/mediautils";
import { OPEN_MEDIA_FILTERS_EVENT } from "@/components/Header/HeaderSearch";

interface IProps {
  isGrid?: boolean;
  fromMediaList?: boolean;
  limit?: number;
  propsFilters?: any;
  propsHandleSetFilters?: (filters: any) => void;
  propsHiddenFilters?: Record<string, any[]>;
  propsAvailableFilters?: Filter[];
  propsGetListMedia?: (params: any) => void;
  isLoading?: boolean;
}

function countAppliedFilters(filters: Record<string, any>): number {
  const ignore = new Set(["page", "rows", "sort", "sortBy", "must_not"]);
  let count = 0;
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (key.endsWith("displayValue")) return;
    if (ignore.has(key)) return;
    if (value == null) return;
    count += 1;
  });
  return count;
}

const Discover = ({
  categoriesConst,
  languageConst,
  orgBusTeamConstants,
  users,
  singleCurrentUser,
  agencies,
  getListMedia,
  mediaList,
  updateModal,
  totalCount,
  isClearFilter,
  fromMediaList,
  limit,
  presetList,
  getDiscoverListMedia,
  setMediaDetails,
  isLoading,
  getDiscoverWiredListMedia,
  bunchBusinessList,
  bunchProductList,
  getListAgencies,
  getListUser,
}: Props) => {
  const skeletonCards = useMemo(() => {
    return [...Array(12)].map((_, index) => (
      <div key={index} className="col-sm-6 col-md-4 col-lg-3 col-xl-2 mb-4">
        <div className="card shadow-sm">
          <div className="position-relative card-img-top">
            <div className="rounded ratio ratio-16x9 border bg-secondary w-100">
              <Skeleton type="thumbnail" width="100%" height="100%" />
            </div>
          </div>
          <div className="card-body py-2" style={{ minHeight: "95px" }}>
            <Skeleton type="title" width="80%" height="20px" className="mb-2" />
            <Skeleton type="text" width="60%" height="16px" className="mb-2" />
            <div className="d-flex align-items-center gap-2">
              <Skeleton type="avatar" width="22px" height="22px" circle />
              <Skeleton type="avatar" width="22px" height="22px" circle />
              <Skeleton type="avatar" width="22px" height="22px" circle />
            </div>
          </div>
          <div className="border-top d-flex">
            <div className="d-flex gap-2 flex-grow-1 justify-content-around">
              <Skeleton type="text" width="40px" height="40px" />
              <Skeleton type="text" width="40px" height="40px" />
              <Skeleton type="text" width="40px" height="40px" />
            </div>
          </div>
        </div>
      </div>
    ));
  }, []);

  const [searchParams] = useSearchParams();

  // Consolidated tab state - activeType and currentTab are the same
  const [activeTab, setActiveTab] = useState<string>(() => {
    const pathname = window.location.pathname;
    const assetTypeFromPath = pathname.split("/").pop();
    const tabParam = searchParams.get("tab");

    if (tabParam) {
      return tabParam;
    }

    if (assetTypeFromPath) {
      if (Object.keys(DISCOVER_TAB_MAPPING).includes(assetTypeFromPath)) {
        return assetTypeFromPath;
      }
    }
    return "wired";
  });

  const [selectedVideo, setSelectedVideo] = useState<MediaModel>(
    {} as MediaModel
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNavTabsDisabled, setIsNavTabsDisabled] = useState(true);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

  useEffect(() => {
    const open = () => setIsFilterSheetOpen(true);
    window.addEventListener(OPEN_MEDIA_FILTERS_EVENT, open);
    return () => window.removeEventListener(OPEN_MEDIA_FILTERS_EVENT, open);
  }, []);

  // Memoize modal content to avoid recreating GeneralPlayerWrapper on every render
  const modalContent = useMemo(() => {
    if (!selectedVideo?.id || !isModalOpen) {
      return null;
    }
    return (
      <GeneralPlayerWrapper
        media={selectedVideo}
        showUsedBy={activeTab === "wired" ? true : false}
        currentTab={selectedVideo?.previewfile ? DRIVE_TAB : undefined}
      />
    );
  }, [selectedVideo, activeTab, isModalOpen]);

  // Use the custom hook for filters
  const {
    filters,
    handleSetFilters,
    availableFilters,
    hiddenFilter,
    handleHiddenFilter: handleHiddenFilterFromHook,
    onAutoSelectInputChange,
    autoSelectOptionsMap,
  } = useDiscoverFilters({
    categoriesConst,
    languageConst,
    orgBusTeamConstants,
    users,
    singleCurrentUser,
    agencies,
    bunchProductList,
    bunchBusinessList,
    fromMediaList,
    activeTab,
    setActiveTab,
    getListAgencies,
    getListUser,
  });

  useEffect(() => {
    store.dispatch(
      setMediaList({ data: { list: [], count: 0 }, isLoading: true })
    );
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsNavTabsDisabled(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsNavTabsDisabled(true);
    }
  }, [isLoading]);

  useEffect(() => {
    const requestParams = {
      ...filters,
      ...hiddenFilter,
      rows: Number(filters?.rows || 60),
      page: Number(filters?.page || 1),
    };

    if (activeTab === "wired") {
      getDiscoverWiredListMedia(requestParams);
    } else {
      getDiscoverListMedia(requestParams);
    }
  }, [
    activeTab,
    filters,
    hiddenFilter,
    getDiscoverListMedia,
    getDiscoverWiredListMedia,
  ]);

  const handlePreview = useCallback((item: MediaModel) => {
    setSelectedVideo(item);
    setIsModalOpen(true);
  }, []);

  const onFilterChange = (_key: string, _value: number) => {
    //
  };

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedVideo({} as MediaModel);
  }, []);
  const handleTrim = useCallback(() => {
    setMediaDetails({
      data: {
        media: selectedVideo,
      },
    });
    const trimEvent = new CustomEvent("triggerTrim", {
      detail: { videoId: selectedVideo.id },
    });
    window.dispatchEvent(trimEvent);
    setTimeout(() => {
      handleModalClose();
    }, 100);
  }, [selectedVideo, setMediaDetails, handleModalClose]);

  const handleClone = useCallback(() => {
    setMediaDetails({
      data: {
        media: selectedVideo,
      },
    });
    const cloneEvent = new CustomEvent("triggerClone", {
      detail: { videoId: selectedVideo.id },
    });
    window.dispatchEvent(cloneEvent);
    setTimeout(() => {
      handleModalClose();
    }, 100);
  }, [selectedVideo, setMediaDetails, handleModalClose]);

  const handleDownload = useCallback(() => {
    setMediaDetails({
      data: {
        media: selectedVideo,
      },
    });
    const downloadEvent = new CustomEvent("triggerDownload", {
      detail: { videoId: selectedVideo.id },
    });
    window.dispatchEvent(downloadEvent);
    setTimeout(() => {
      handleModalClose();
    }, 100);
  }, [selectedVideo, setMediaDetails, handleModalClose]);

  const handleHiddenFilter = (e: any, filter: any) => {
    e.preventDefault();

    if (isNavTabsDisabled) {
      return;
    }

    const tabLabel =
      DISCOVER_NAV_TABS.find((t: any) => t.key === filter)?.label || filter;
    trackEvent("browse", {
      category: "filters",
      entity: tabLabel,
      action: "click",
      location: "discover",
      ...getTrackUserContext(),
    });

    handleHiddenFilterFromHook(e, filter);
  };

  return (
    <>
      <div className="discover">
        {!limit && (
          <>
            <div className="subHeader d-flex justify-content-between align-items-center p-3 z-4">
              {!fromMediaList && (
                <>
                  <ul
                    className="nav nav-underline gap-3 mt-1"
                    style={{ marginBottom: "-14px" }}
                  >
                    {DISCOVER_NAV_TABS.map((item: any) => {
                      return (
                        <li className="nav-item" key={item.key}>
                          <a
                            className={`nav-link px-2 py-2 gap-1 f-14 lh-14 ${
                              activeTab === item.key ? "active" : ""
                            } ${isNavTabsDisabled ? "disabled" : ""}`}
                            href="#"
                            key={item.key}
                            onClick={(e) => {
                              if (!isNavTabsDisabled) {
                                handleHiddenFilter(e, item.key);
                              }
                            }}
                            style={{
                              pointerEvents: isNavTabsDisabled
                                ? "none"
                                : "auto",
                              opacity: isNavTabsDisabled ? 0.5 : 1,
                            }}
                          >
                            {item.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
            <div className="trendingChip d-flex p-3 gap-2 align-items-center H">
              <strong className="pe-1">Trending:</strong>
              <div className="social-chip d-flex align-items-center bg-white border gap-1 px-3 rounded-pill pointer">
                <span className="text-truncate mx-1 d-inline-block me-1 align-middle">
                  Sports
                </span>
              </div>
            </div>
          </>
        )}
        {!limit && (
          <FilterRail
            isOpen={isFilterSheetOpen}
            count={countAppliedFilters(filters)}
            onToggle={() => setIsFilterSheetOpen((v) => !v)}
          />
        )}
        {!limit && (
          <AppliedFilterChips
            appliedFilters={filters}
            availableFilters={availableFilters}
            onRemove={(next) => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              handleSetFilters(next);
            }}
            onClearAll={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              handleSetFilters({});
            }}
            onOpenSheet={() => setIsFilterSheetOpen(true)}
            singleCurrentUser={singleCurrentUser}
          />
        )}
        {!limit && (
          <FilterSheet
            show={isFilterSheetOpen}
            onHide={() => setIsFilterSheetOpen(false)}
            availableFilters={availableFilters}
            appliedFilters={filters}
            onApply={(next) => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              handleSetFilters(next);
            }}
            onClear={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              handleSetFilters({});
            }}
            allowedTeamIds={getAllowedTeamIds(singleCurrentUser?.permissions)}
            onAutoSelectInputChange={onAutoSelectInputChange}
            autoSelectOptionsMap={autoSelectOptionsMap}
            autoSelectLoader={true}
            currentUser={singleCurrentUser}
          />
        )}
        <div className="d-flex mt-3">
          {mediaList.isLoading ? (
            <div className="mediaedit_content_form px-3 flex-grow-1">
              <div className="row gy-4">{skeletonCards}</div>
            </div>
          ) : mediaList.list.length === 0 ? (
            <NoContentFound message="No Content Available" />
          ) : (
            <div className="mediaedit_content_form px-3 flex-grow-1">
              <div className="row gy-4 pb-5 mb-4">
                {(limit
                  ? mediaList?.list.slice(0, limit)
                  : mediaList?.list
                ).map((item) => (
                  <div
                    key={item.id}
                    className={`${
                      limit
                        ? "col-12 col-md-4"
                        : "col-sm-6 col-md-4 col-lg-3 col-xl-2"
                    }`}
                  >
                    <DiscoverCard
                      item={item}
                      onPreview={handlePreview}
                      useProgressiveLoading={true}
                      presetList={presetList}
                      productList={bunchProductList}
                      showUsedBy={activeTab === "wired" ? true : false}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {!fromMediaList && !limit ? (
        <Pagination
          total={totalCount}
          onChange={onFilterChange}
          handleSetFilters={handleSetFilters}
          isFilter={true}
          ListMediaApi={singleCurrentUser?.team ? getListMedia : null}
          filters={filters}
          isClearFilter={isClearFilter}
          align="left"
          showRowsPerPage={true}
          rowsPerPageOptions={[60, 120]}
          minRowsPerPage={60}
        />
      ) : null}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={selectedVideo?.title}
          closeOnOutsideClick={true}
          width="990px"
          height="auto"
          updateModal={updateModal}
          footer={
            <div className="d-flex gap-2 justify-content-end">
              <Tooltip content="Download">
                <button
                  className="btn btn-outline-primary btn-sm d-flex gap-2 align-items-center"
                  onClick={handleDownload}
                  title="Download"
                >
                  <Download size={16} />
                  <span>Download</span>
                </button>
              </Tooltip>
              <Tooltip content="Clone">
                <button
                  className="btn btn-outline-primary btn-sm d-flex gap-2 align-items-center"
                  onClick={handleClone}
                  title="Clone"
                  disabled={
                    !selectedVideo?.meta?.duration ||
                    selectedVideo?.meta?.duration <= 0
                  }
                >
                  <Layers2 size={16} />
                  <span>Clone</span>
                </button>
              </Tooltip>

              <Tooltip content="Trim">
                <button
                  className="btn btn-primary btn-sm d-flex gap-2 align-items-center"
                  onClick={handleTrim}
                  title="Trim"
                  disabled={
                    !selectedVideo?.meta?.duration ||
                    selectedVideo?.meta?.duration <= 0
                  }
                >
                  <Scissors size={16} />
                  <span>Trim</span>
                </button>
              </Tooltip>
            </div>
          }
        >
          {modalContent}
        </Modal>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  categoriesConst: state.app.categories,
  languageConst: state.app.metaLanguage,
  isLoading: state.media.mediaList.isLoading,
  orgBusTeamConstants: state.app.orgBusTeamConstants,
  users: state.app.constUserList,
  singleCurrentUser: state.users.singleCurrentUser,
  bunchBusinessList: state.constants.bunchBusinessList.list,
  bunchProductList: state.constants.bunchProductList.list,
  agencies: state.constants.agenciesList,
  mediaList: state.media.mediaList,
  totalCount: state.media.mediaList.count,
  isClearFilter: state.media.isClearFilter,
  presetList: state.media.presetList.list,
  previousFilters: state.media.previousFilters,
  preMediaList: state.media.preMediaList.list,
  postMediaList: state.media.postMediaList.list,
});

const mapDispatch = {
  getDiscoverListMedia,
  getDiscoverWiredListMedia,
  getListMedia,
  updateModal,
  getUserSettings,
  updatePreviousFilters,
  setMediaDetails,
  getListAgencies,
  getListUser,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & IProps;

export default connector(Discover);
